import { Helmet } from 'react-helmet-async'

import { useSetState } from '@domain-group/react-hooks'

import {
  Box, Columns, ConditionalContent, LoadingModule, PageHeader,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCurrentUser from '@hooks/useCurrentUser'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'

import type { CampaignModel, OrderItemModel } from '@models/types'

import generateMediaAndCopyTabBarItems from '@sections/Client/packs/Campaign/utils/generateMediaAndCopyTabBarItems'

import { getCampaignPermissions, getCampaignType } from '@functions/campaign'
import useMediaSelector from '../../hooks/useMediaSelector'

import MediaSelectionBlankState from '../MediaSelectionBlankState'
import MediaSelectionTools from '../MediaSelectionTools'
import OrderItem from '../OrderItem'
import PortalMedia from '../PortalMedia'
import UserGuideBanner from '../UserGuideBanner'

const defaultViewState = {
  viewMode: 'small' as 'small' | 'large',
}

const PageContent = ({ campaign }: { campaign: CampaignModel }) => {
  const mediaSelectionPayload = useMediaSelector(campaign)
  const {
    filteredOrderItems,
    hasFilteredOrderItems,
    hasFilteredPortals,
    isMediaSelector,
    loading,
  } = mediaSelectionPayload || {}

  const [viewState, setViewState] = useSetState(defaultViewState)
  const { viewMode } = viewState
  const setViewMode = (newViewMode: 'small' | 'large') => setViewState({ viewMode: newViewMode })

  const { address } = campaign || {}

  const { currentUser } = useCurrentUser()
  const campaignPermissions = getCampaignPermissions({
    user: currentUser,
    campaignType: getCampaignType(campaign),
  })

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Media Selection</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="media-selection"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        nestedNavigation
        tabBarItems={generateMediaAndCopyTabBarItems(campaign.id, campaignPermissions)}
        title="Media Selection"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            <NotificationBannerLocation locationKey="agency.campaign.mediaSelection.banner" />

            <ConditionalContent visible={!loading}>
              {!hasFilteredOrderItems && !hasFilteredPortals && <MediaSelectionBlankState />}

              {isMediaSelector && hasFilteredOrderItems && <UserGuideBanner campaign={campaign} />}

              {hasFilteredPortals && <PortalMedia campaign={campaign} viewMode={viewMode} />}

              {filteredOrderItems.map((orderItem: OrderItemModel) => (
                <OrderItem key={orderItem.id} orderItem={orderItem} viewMode={viewMode} />
              ))}
            </ConditionalContent>
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.mediaSelection.sidebar" />

            <MediaSelectionTools campaign={campaign} isMediaSelector={isMediaSelector} setViewMode={setViewMode} viewMode={viewMode}/>
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
